import * as THREE from "three";
import { EXRLoader, RGBELoader } from "three-stdlib";
import { getSceneState } from "../stores/sceneStore";
import { getAssetById } from "../stores/assetStore";
import { getHFieldWithAppliedHeightScale, getStandardFloorHField } from "../utils/terrainFloor";
import {
  createHeightFieldContourLines,
  createHeightFieldGeometry,
  createHeightFieldSurfaceGrid,
} from "../utils/terrainHeightFieldGeometry";
import {
  getFloorAppearanceOptions,
  getFloorAppearancePalette,
} from "../utils/floorAppearanceOptions";
import { getTerrainVisualizationOptions } from "../utils/terrainVisualizationOptions";
import { getSceneHdriUrls } from "../utils/sceneHdri";
import { logger } from "../utils/logger";
import { getBytes } from "../utils/idb";
import {
  DEFAULT_VIEWPORT_LIGHTS,
  getViewportLightingOptions,
} from "../utils/defaultViewportLights";
import {
  getSceneHdriBackgroundVisible,
  SCENE_PLAIN_BACKGROUND_COLOR,
} from "../utils/sceneHdriBackgroundVisibility";

/** Edge length of the default square scene floor mesh (Three.js `PlaneGeometry` width/height). */
export const DEFAULT_SCENE_FLOOR_SIZE = 20;
const MAX_SCENE_BACKGROUND_WIDTH = 4096;
const MAX_SCENE_BACKGROUND_HEIGHT = 2048;

function showSceneBackgroundToast(message) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("critter-toast", { detail: { message } }));
}

function createStandardFloorGeometry(floorSize, hfieldDef = null, visualizationOptions = null) {
  const heatmapColorMode = visualizationOptions?.heatmapColorMode === "height" ? "height" : "slope";
  if (hfieldDef) {
    const def = getHFieldWithAppliedHeightScale(hfieldDef);
    return createHeightFieldGeometry(def.size, def, { heatmapColorMode });
  }
  return new THREE.PlaneGeometry(floorSize, floorSize);
}

function disposeObject3D(object) {
  object.traverse((child) => {
    child.geometry?.dispose?.();
    if (Array.isArray(child.material)) {
      child.material.forEach((material) => material.dispose?.());
    } else {
      child.material?.dispose?.();
    }
  });
}

function updateStandardFloorContours(floor, hfieldDef, visualizationOptions) {
  const existingContours = floor.getObjectByName("sceneFloorContours");
  if (existingContours) {
    floor.remove(existingContours);
    disposeObject3D(existingContours);
  }

  if (!hfieldDef || !visualizationOptions.showContours) return;
  const contours = createHeightFieldContourLines(hfieldDef.size, hfieldDef);
  if (contours) floor.add(contours);
}

function updateStandardFloorSurfaceGrid(floor, hfieldDef, visualizationOptions) {
  const existing = floor.getObjectByName("sceneFloorSurfaceGrid");
  if (existing) {
    floor.remove(existing);
    disposeObject3D(existing);
  }

  if (!visualizationOptions.showSurfaceGrid || !hfieldDef) return;

  /** Only follow terrain height — never duplicate the scene `sceneGrid` helper with a flat overlay. */
  const grid = createHeightFieldSurfaceGrid(hfieldDef.size, hfieldDef);
  if (grid) floor.add(grid);
}

function applyLineObjectColor(object, color) {
  if (!object) return;
  object.traverse((child) => {
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      if (!material?.color) return;
      material.color.setHex(color);
      material.needsUpdate = true;
    });
  });
}

function applyGridHelperColors(grid, centerColor, lineColor) {
  const colorAttribute = grid?.geometry?.getAttribute?.("color");
  if (!colorAttribute) return false;
  const divisions = Math.max(1, Math.floor(colorAttribute.count / 4) - 1);
  const center = divisions / 2;
  const centerThreeColor = new THREE.Color(centerColor);
  const lineThreeColor = new THREE.Color(lineColor);
  for (let i = 0; i <= divisions; i += 1) {
    const color = i === center ? centerThreeColor : lineThreeColor;
    const startVertex = i * 4;
    for (let j = 0; j < 4; j += 1) {
      colorAttribute.setXYZ(startVertex + j, color.r, color.g, color.b);
    }
  }
  colorAttribute.needsUpdate = true;
  return true;
}

export function applyStandardFloorAppearance(scene, options = getFloorAppearanceOptions()) {
  const palette = getFloorAppearancePalette(options);
  const floor = scene?.getObjectByName("sceneFloor");
  if (floor?.isMesh && floor.material?.color) {
    const material = floor.material;
    material.color.setHex(palette.floorColor);
    if (typeof material.metalness === "number") material.metalness = palette.metalness;
    if (typeof material.roughness === "number") material.roughness = palette.roughness;
    if (typeof material.reflectivity === "number") material.reflectivity = palette.reflectivity;
    if (typeof material.clearcoat === "number") material.clearcoat = palette.clearcoat;
    if (typeof material.clearcoatRoughness === "number") {
      material.clearcoatRoughness = palette.clearcoatRoughness;
    }
    if (typeof material.opacity === "number") material.opacity = palette.floorOpacity;
    material.transparent = palette.floorOpacity < 1;
    material.depthWrite = palette.floorOpacity >= 1;
    material.needsUpdate = true;
  }

  const grid = scene?.getObjectByName("sceneGrid");
  if (grid) {
    const updatedVertexColors = applyGridHelperColors(
      grid,
      palette.gridCenterColor,
      palette.gridLineColor
    );
    const gridMaterials = Array.isArray(grid.material) ? grid.material : [grid.material];
    const [centerMaterial, lineMaterial] = gridMaterials;
    if (!updatedVertexColors && centerMaterial?.color) {
      centerMaterial.color.setHex(palette.gridCenterColor);
      centerMaterial.needsUpdate = true;
    }
    if (!updatedVertexColors && lineMaterial?.color) {
      lineMaterial.color.setHex(palette.gridLineColor);
      lineMaterial.needsUpdate = true;
    }
  }

  applyLineObjectColor(floor?.getObjectByName("sceneFloorSurfaceGrid"), palette.lineColor);
  applyLineObjectColor(floor?.getObjectByName("sceneFloorContours"), palette.lineColor);
}

export function applyStandardFloorTerrain(
  scene,
  floorSize,
  hfieldDef = null,
  visualizationOptions = getTerrainVisualizationOptions()
) {
  const floor = scene.getObjectByName("sceneFloor");
  if (!floor?.isMesh) return;
  const effectiveHField = hfieldDef ? getHFieldWithAppliedHeightScale(hfieldDef) : null;
  const nextGeometry = createStandardFloorGeometry(floorSize, hfieldDef, visualizationOptions);
  try {
    floor.geometry?.dispose?.();
  } catch {
    // Keep scene responsive if Three.js disposal fails.
  }
  floor.geometry = nextGeometry;
  if (floor.material) {
    floor.material.vertexColors = Boolean(
      visualizationOptions.showHeatmap && nextGeometry.getAttribute("color")
    );
    floor.material.needsUpdate = true;
  }
  const legacyFlow = floor.getObjectByName("sceneFloorFlowArrows");
  if (legacyFlow) {
    floor.remove(legacyFlow);
    disposeObject3D(legacyFlow);
  }
  updateStandardFloorContours(floor, effectiveHField, visualizationOptions);
  updateStandardFloorSurfaceGrid(floor, effectiveHField, visualizationOptions);
  applyStandardFloorAppearance(scene);
  floor.userData = {
    ...floor.userData,
    terrainHFieldName: hfieldDef?.name || "",
  };
}

export function applySceneHdriEnvironment(scene, options = getFloorAppearanceOptions()) {
  if (!scene) return;
  const sceneBackground = getSceneState()?.background;
  const requestKey =
    sceneBackground?.type === "color"
      ? `color:${sceneBackground.colorHex || ""}`
      : sceneBackground?.type === "asset" && sceneBackground.assetId
        ? `asset:${sceneBackground.assetId}`
        : `builtin:${getSceneHdriUrls(options).join("|")}`;
  scene.userData.sceneHdriRequestKey = requestKey;

  const disposePreviousEnvironmentMap = () => {
    const previousMap = scene.userData.sceneHdriTexture;
    const previousObjectUrl = scene.userData.sceneHdriObjectUrl;
    if (previousMap) previousMap.dispose();
    if (previousObjectUrl) URL.revokeObjectURL(previousObjectUrl);
    delete scene.userData.sceneHdriTexture;
    delete scene.userData.sceneHdriObjectUrl;
  };

  const applyLoadedEnvironmentMap = (map, objectUrl = "") => {
    if (scene.userData.sceneHdriRequestKey !== requestKey) {
      map.dispose();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      return;
    }
    const image = map?.image || {};
    const width = Number(image.width) || 0;
    const height = Number(image.height) || 0;
    if (width > MAX_SCENE_BACKGROUND_WIDTH || height > MAX_SCENE_BACKGROUND_HEIGHT) {
      map.dispose();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      logger.warn("[sceneDefaults] Scene background is too large for the viewport renderer", {
        width,
        height,
        maxWidth: MAX_SCENE_BACKGROUND_WIDTH,
        maxHeight: MAX_SCENE_BACKGROUND_HEIGHT,
      });
      showSceneBackgroundToast(
        `Background is ${width}x${height}. Use a 4K or smaller equirectangular image under 50 MB.`
      );
      return;
    }
    map.mapping = THREE.EquirectangularReflectionMapping;
    if ("colorSpace" in map && map.isTexture && !map.isDataTexture) {
      map.colorSpace = THREE.SRGBColorSpace;
    }
    const previousMap = scene.userData.sceneHdriTexture;
    const previousObjectUrl = scene.userData.sceneHdriObjectUrl;
    scene.environment = map;
    scene.background = getSceneHdriBackgroundVisible()
      ? map
      : new THREE.Color(SCENE_PLAIN_BACKGROUND_COLOR);
    scene.userData.sceneHdriTexture = map;
    scene.userData.sceneHdriObjectUrl = objectUrl;
    if (previousMap && previousMap !== map) previousMap.dispose();
    if (previousObjectUrl && previousObjectUrl !== objectUrl)
      URL.revokeObjectURL(previousObjectUrl);
    logger.info("[sceneDefaults] HDRI environment map applied");
  };

  const applyColorBackground = (background) => {
    const match = /^#?([0-9a-f]{6})$/i.exec(String(background?.colorHex || "").trim());
    const colorHex = match ? `#${match[1]}` : "#111827";
    disposePreviousEnvironmentMap();
    scene.environment = null;
    scene.background = new THREE.Color(colorHex);
    logger.info("[sceneDefaults] Plain color scene background applied");
  };

  const loadBuiltInHdri = (index = 0) => {
    const rgbeLoader = new RGBELoader();
    const hdriUrls = getSceneHdriUrls(options);
    const url = hdriUrls[index];
    if (!url) return;
    rgbeLoader.load(
      url,
      (hdrMap) => {
        applyLoadedEnvironmentMap(hdrMap);
      },
      undefined,
      () => {
        if (index + 1 >= hdriUrls.length) {
          logger.warn("[sceneDefaults] Failed to load HDRI environment map", url);
          return;
        }
        loadBuiltInHdri(index + 1);
      }
    );
  };

  const loadAssetBackground = async (background) => {
    const asset = getAssetById(background.assetId);
    if (!asset) {
      logger.warn("[sceneDefaults] Scene background asset is not registered", {
        assetId: background.assetId,
      });
      return;
    }

    const sources = asset.sources || {};
    let url = sources.blobUrl || sources.url || "";
    let objectUrl = "";
    if (!url && sources.idbKey) {
      const blob = await getBytes(sources.idbKey);
      if (blob) {
        objectUrl = URL.createObjectURL(blob);
        url = objectUrl;
      }
    }
    if (!url) {
      logger.warn("[sceneDefaults] Scene background asset has no readable source", {
        assetId: background.assetId,
      });
      return;
    }
    if (scene.userData.sceneHdriRequestKey !== requestKey) {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      return;
    }

    const backgroundFileName = background.fileName || asset.name || url;
    const isHdr =
      /\.hdr(?:$|[?#])/i.test(backgroundFileName) || background.mimeType === "image/vnd.radiance";
    const isExr =
      /\.exr(?:$|[?#])/i.test(backgroundFileName) ||
      background.mimeType === "image/x-exr" ||
      background.mimeType === "image/exr";
    const loader = isExr ? new EXRLoader() : isHdr ? new RGBELoader() : new THREE.TextureLoader();
    loader.load(
      url,
      (texture) => applyLoadedEnvironmentMap(texture, objectUrl),
      undefined,
      (error) => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        logger.warn("[sceneDefaults] Failed to load scene background asset", {
          assetId: background.assetId,
          error,
        });
      }
    );
  };

  if (sceneBackground?.type === "asset" && sceneBackground.assetId) {
    void loadAssetBackground(sceneBackground);
    return;
  }
  if (sceneBackground?.type === "color") {
    applyColorBackground(sceneBackground);
    return;
  }

  loadBuiltInHdri();
}

/**
 * Adds default lighting, floor, and grid to a Three.js scene
 * Creates a clean lab-like environment suitable for robot visualization
 *
 * @param {THREE.Scene} scene - The scene to add defaults to
 * @param {Object} options - Configuration options
 * @param {number} options.ambientIntensity - Ambient light intensity (default: 0.4)
 * @param {number} options.hemiLightIntensity - Hemisphere light intensity (default: 0.7)
 * @param {number} options.floorSize - Size of the floor plane (default: 20)
 * @param {number} options.gridSize - Size of the grid helper (default: same as floor)
 * @param {number} options.gridDivisions - Number of grid divisions (default: 20)
 * @param {boolean} options.enableShadows - Whether to enable shadows (default: true)

 */
export function addDefaultScene(scene, options = {}) {
  const viewportLights = getViewportLightingOptions();
  const {
    ambientIntensity = viewportLights.ambient.intensity,
    hemiLightIntensity = viewportLights.hemisphere.intensity,
    floorSize = DEFAULT_SCENE_FLOOR_SIZE,
    gridSize: gridSizeOption,
    gridDivisions = 20,
    enableShadows = true,
  } = options;

  const gridSize = gridSizeOption ?? floorSize;

  // Ambient light
  const ambientLight = new THREE.AmbientLight(viewportLights.ambient.color, ambientIntensity);
  ambientLight.name = DEFAULT_VIEWPORT_LIGHTS.ambient.name;
  ambientLight.userData = { isViewportLight: true, viewportLightKind: "ambient" };
  scene.add(ambientLight);

  // Hemisphere light
  const hemiLight = new THREE.HemisphereLight(
    viewportLights.hemisphere.skyColor,
    viewportLights.hemisphere.groundColor,
    hemiLightIntensity
  );
  hemiLight.name = DEFAULT_VIEWPORT_LIGHTS.hemisphere.name;
  hemiLight.userData = { isViewportLight: true, viewportLightKind: "hemisphere" };
  hemiLight.position.set(...DEFAULT_VIEWPORT_LIGHTS.hemisphere.position);
  scene.add(hemiLight);

  const standardFloorHField = getStandardFloorHField(getSceneState()?.hfields);
  const standardFloorEffective = standardFloorHField
    ? getHFieldWithAppliedHeightScale(standardFloorHField)
    : null;
  const terrainVisualizationOptions = getTerrainVisualizationOptions();
  const floorAppearanceOptions = getFloorAppearanceOptions();
  const floorAppearancePalette = getFloorAppearancePalette(floorAppearanceOptions);
  // Floor
  const floorGeometry = createStandardFloorGeometry(
    floorSize,
    standardFloorHField,
    terrainVisualizationOptions
  );
  const floorMaterial = new THREE.MeshPhysicalMaterial({
    color: floorAppearancePalette.floorColor,
    metalness: floorAppearancePalette.metalness,
    roughness: floorAppearancePalette.roughness,
    reflectivity: floorAppearancePalette.reflectivity,
    clearcoat: floorAppearancePalette.clearcoat,
    clearcoatRoughness: floorAppearancePalette.clearcoatRoughness,
    opacity: floorAppearancePalette.floorOpacity,
    transparent: floorAppearancePalette.floorOpacity < 1,
    depthWrite: floorAppearancePalette.floorOpacity >= 1,
    vertexColors: Boolean(
      terrainVisualizationOptions.showHeatmap && floorGeometry.getAttribute("color")
    ),
  });
  const floor = new THREE.Mesh(floorGeometry, floorMaterial);
  floor.rotation.x = -Math.PI / 2;
  // After rotating into XZ, the ground plane is at y=0 (Three.js Y-up).
  floor.position.y = -0.001;
  if (enableShadows) {
    floor.receiveShadow = true;
  }
  floor.name = "sceneFloor";
  floor.userData = {
    ...floor.userData,
    terrainHFieldName: standardFloorHField?.name || "",
  };
  updateStandardFloorContours(floor, standardFloorEffective, terrainVisualizationOptions);
  updateStandardFloorSurfaceGrid(floor, standardFloorEffective, terrainVisualizationOptions);
  scene.add(floor);

  // Grid helper — same span as the floor so lines reach the edges after terrain fills the patch.
  const gridHelper = new THREE.GridHelper(
    gridSize,
    gridDivisions,
    floorAppearancePalette.gridCenterColor,
    floorAppearancePalette.gridLineColor
  );
  const gridMats = Array.isArray(gridHelper.material) ? gridHelper.material : [gridHelper.material];
  gridMats.forEach((mat) => {
    mat.transparent = true;
    mat.opacity = 0.6;
    mat.depthWrite = false;
  });
  gridHelper.name = "sceneGrid";
  gridHelper.position.y = 0.002;
  scene.add(gridHelper);
  applyStandardFloorAppearance(scene, floorAppearanceOptions);

  // --- Dramatic HDRI Environment Map ---
  applySceneHdriEnvironment(scene, floorAppearanceOptions);
}
